import { isWeb } from "@helpers/platform";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import type {
	OriginalStimulusTypes,
	Professor,
	SkillTypes,
	Slide,
} from "@memorang/types/src/session/new-session";
import { Box, VideoPlayer, ZoomImage, useDevice } from "@memorang/ui";
import InfoAlert from "components/InfoAlert";
import { ScrollView } from "react-native";
import { Text } from "react-native-paper";
import AudioPlayerContainer from "../containers/AudioPlayerContainer";
import DiscussionView from "./DiscussionView";
import ItemStem from "./ItemStem";
import { PassageStem } from "./PassageStem";
import SlideShowRenderer from "./SlideShowRenderer";
import StimulusInfo from "./StimulusInfo";
import StimulusTabContent from "./StimulusTabContent";
import EMRCard from "./emr-card/EMRCard";

type Props = {
	stem: string;
	audio?: string;
	toggleShowReadMore?: () => void;
	highlightedHtml?: string;
	id?: number;
	handleStoreHtml?: (html: string) => void;
	isSummativeTest?: boolean;
	index?: number;
	handleClickInsertableText: (choiceId: string) => void;
	numPassageItems?: number;
	parentTitle?: string;
	parentInstruction?: string;
	parentType: OriginalStimulusTypes;
	skill: SkillTypes;
	disableInsertableText?: boolean;
	slideshowData?: { image: string; audio: string }[];
	professor?: Professor;
	slides?: Slide[];
	isSlidesHasPassage?: boolean;
	parentVideo?: string;
	parentImage?: string;
	video?: string;
};
const PassageStemContainer = ({
	stem,
	audio,
	toggleShowReadMore,
	highlightedHtml,
	id,
	index,
	isSummativeTest,
	handleClickInsertableText,
	numPassageItems,
	parentTitle,
	parentInstruction,
	parentType,
	slideshowData,
	professor,
	slides,
	isSlidesHasPassage,
	skill,
	disableInsertableText,
	video,
	parentVideo,
	parentImage,
}: Props) => {
	const { isMobile, isMediumWindowSize } = useDevice();
	const theme = useAppTheme();

	const { currentExam } = useDemoExamStore();

	if (isMobile || isMediumWindowSize) {
		return (
			<Box
				paddingBottom={isSummativeTest ? 0 : 16}
				paddingTop={isSummativeTest ? 16 : 0}
			>
				<PassageStem
					parentStem={stem}
					parentAudio={audio}
					index={index}
					numPassageItems={numPassageItems}
					parentTitle={parentTitle}
					toggleShowReadMore={toggleShowReadMore}
					parentType={parentType}
					itemId={id!}
					video={video}
				/>
			</Box>
		);
	}

	return (
		<ScrollView showsVerticalScrollIndicator={false}>
			<Box
				// borderRightWidth={1}
				// borderRightColor={theme.colors.outlineVariant}
				paddingRight={isWeb ? 16 : 0}
				height={"100%"}
				gap={16}
				paddingBottom={100}
				style={{ overflowY: "auto" }}
			>
				{isSlidesHasPassage && slides && slides.length > 0 ? (
					<StimulusTabContent
						padding={0}
						parentTitle={parentTitle!}
						slideshowData={slideshowData!}
						parentIndex={index!}
						slides={slides!}
						parentInstruction={parentInstruction!}
						itemId={id}
					/>
				) : (
					<>
						<StimulusInfo skill={skill} />
						{parentInstruction && index === 1 && (
							<InfoAlert
								text={parentInstruction}
								backgroundColor={theme.colors.primaryContainer}
							/>
						)}
						<Text variant="titleLarge">{parentTitle}</Text>
						{professor && <DiscussionView professor={professor} />}

						{slideshowData && slideshowData.length > 0 && (
							<SlideShowRenderer slideshowData={slideshowData} itemId={id} />
						)}
						{stem && (
							<ItemStem
								text={stem}
								highlightedHtml={highlightedHtml}
								key={id}
								handleClickInsertableText={handleClickInsertableText}
								isSummativeTest={isSummativeTest}
								disableInsertableText={disableInsertableText}
							/>
						)}

						{parentVideo && <VideoPlayer videoUrl={parentVideo} />}
						{parentImage && (
							<ZoomImage
								key={id}
								url={parentImage}
								allImages={[
									{
										url: parentImage,
									},
								]}
							/>
						)}
						{audio && <AudioPlayerContainer uri={audio} itemId={id} />}
						{/* {video && <VideoPlayer videoUrl={video} />} */}
						{currentExam.showEMR && (
							<Box padding={2}>
								<EMRCard />
							</Box>
						)}
					</>
				)}
			</Box>
		</ScrollView>
	);
};

export default PassageStemContainer;
